import Vue from 'utils/vueUtils';
import moment from 'moment';
import Keywords from '../collections/Keywords';
import Keyword from '../models/Keyword';
import Contacts from '../collections/Contacts';
import sorting from '../../utils/sorting';
import { baseApi } from 'core/api';

const state = () => ({
    _items: [],
    _itemsLoading: true,
    _itemsError: false,
    _pagination: null,
    _parameters: {
        sortBy: null,
        sortOrder: null,
        term: null,
        filtersList: [],
        limit: 10,
        page: 1,
    },
    _keywordContacts: [],
    _keywordContactsId: null,
    _keywordContactsLoading: false,
    _keywordContactsError: false,
    _keywordContactsPagination: {},
    _keywordContactsParameters: {
        page: 1,
        sortBy: ['disabled', 'created_at'],
        sortOrder: 'desc',
        term: null,
        filtersList: [],
        length: 10,
        from: '',
        to: '',
    },
    _keywordReserveWords: [],
});

const getters = {
    keywords: (state) => state._items,
    keywordsLoading: (state) => state._itemsLoading,
    keywordsError: (state) => state._itemsError,
    keywordsPagination: (state) => state._pagination,
    keywordsParameters: (state) => state._parameters,

    keywordContacts: (state) => state._keywordContacts,
    keywordContactsLoading: (state) => state._keywordContactsLoading,
    keywordContactsError: (state) => state._keywordContactsError,
    keywordContactsPagination: (state) => state._keywordContactsPagination,
    keywordContactsParameters: (state) => state._keywordContactsParameters,
    keywordReserveWords: (state) => state._keywordReserveWords,
};

const actions = {
    // keywords
    keywordReserveWordsLoad() {
        return baseApi.get('core/keywords/mandated').then((response) => {
            this._setKeywordsReserveWords(response.data);
            return response.data;
        });
    },

    keywordsLoad(loading = true) {
        if (loading) {
            this._setKeywordsLoading(true);
        }
        return baseApi
            .get('core/keywords', { params: this._parameters })
            .then((response) => {
                this._setKeywordsLoaded(response.data);

                return response.data;
            })
            .catch(() => {
                this._setKeywordsError();
            })
            .finally(() => {
                this._setKeywordsLoading(false);
            });
    },

    keywordsCustomUpdateLengthSelector(data) {
        this._setKeywordUpdateLengthSelector(data);
        this.keywordsLoad();
    },

    keywordsClearSearch(shouldUpdate = true) {
        this._setKeywordsSearchCleared();

        if (shouldUpdate) {
            this.keywordsLoad();
        }
    },

    keywordsUpdateSearch(search) {
        this._setKeywordsUpdateSearch(search);
        this.keywordsLoad();
    },

    keywordsReset() {
        this._setKeywordsReset();
    },

    keywordsUpdatePage(page) {
        this._setKeywordsUpdatePage(page);
        this.keywordsLoad();
    },

    keywordsUpdateLength(length) {
        this._setKeywordsUpdateLength(length);
        this.keywordsLoad();
    },

    keywordsUpdateStatus(status) {
        this._setKeywordsUpdateStatus(status);
        this.keywordsLoad();
    },

    keywordsBulkDelete(form) {
        return form.deleteWithBody('core/keywords').then((response) => {
            this._setKeywordsBulkDeleted();
            this.keywordsLoad();

            return response;
        });
    },

    keywordsUpdateSort(sort) {
        this._setKeywordsUpdateSort(sort);
    },

    // keywordContacts
    keywordContactsLoad({ loading = true, id } = {}) {
        if (loading) {
            this._setKeywordContactsLoading(true);
        }

        const keywordId = id || this._keywordContactsId;

        return baseApi
            .get(`core/keywords/${keywordId}/contacts`, { params: this._keywordContactsParameters })
            .then((response) => {
                this._setKeywordContactsLoaded({
                    data: response.data,
                    id: id,
                });

                return response.data;
            })
            .catch(() => {
                this._setKeywordContactsError();
            })
            .finally(() => {
                this._setKeywordContactsLoading(false);
            });
    },

    keywordContactsUpdateSearch(search) {
        this._setKeywordContactsUpdateSearch(search);
        this.keywordContactsLoad();
    },

    keywordContactsReset() {
        this._setKeywordContactsReset();
    },

    keywordContactsUpdatePage(page) {
        this._setKeywordContactsUpdatePage(page);
        this.keywordContactsLoad();
    },

    keywordContactsUpdateLength(length) {
        this._setKeywordContactsUpdateLength(length);
        this.keywordContactsLoad();
    },

    keywordContactsCustomUpdateLengthSelector(data) {
        this._setKeywordContactsCustomUpdateLengthSelector(data);
        this.keywordContactsLoad();
    },

    keywordContactsUpdateDate(data) {
        this._setKeywordContactsUpdateDate(data);
        this.keywordContactsLoad();
    },

    keywordById(id) {
        return baseApi.get(`core/keywords/${id}`).then((response) => {
            return new Keyword(response.data);
        });
    },

    keywordCreate(form) {
        return form.post('core/keywords').then((response) => {
            return new Keyword(response.data);
        });
    },

    keywordUpdate({ form, id }) {
        return form.put(`core/keywords/${id}`).then((response) => {
            return new Keyword(response.data);
        });
    },

    keywordDelete({ id, form }) {
        this._setKeywordsDelete({ id, type: 'init' });

        return form.delete(`core/keywords/${id}`).then((response) => {
            this._setKeywordsDelete({ id, type: 'delete' });
            this.keywordsLoad(false);

            return response.data;
        });
    },

    keywordToggle({ id, active }) {
        this._setKeywordsToggle({ id, type: 'init' });

        return baseApi.put(`core/keywords/toggle/${id}`, { is_active: active }).then((response) => {
            this._setKeywordsToggle({
                id: id,
                type: 'update',
                keyword: new Keyword(response.data),
            });

            return response.data;
        });
    },

    keywordToggleTest(id) {
        return baseApi.put(`core/keywords/subscribe/toggle/8/${id}`).then((response) => {
            return response.data;
        });
    },

    // Private methods for all operations
    _setKeywordsReserveWords(data) {
        this._keywordReserveWords = data;
    },

    _setKeywordsLoaded(data) {
        this._items = new Keywords(data.data).models;

        const pagination = data.meta;

        this._pagination = {
            current_page: pagination.current_page,
            from: pagination.from,
            last_page: pagination.last_page,
            per_page: pagination.per_page,
            to: pagination.to,
            total: pagination.total,
            next_page_url: pagination.links.next,
        };
    },

    _setKeywordsError() {
        this._items = false;
        this._pagination.total = 0;
    },

    _setKeywordsLoading(status) {
        this._itemsLoading = status;
    },

    _setKeywordsReset() {
        this._items = [];
        this._pagination = {};
        this._itemsLoading = false;
        this._itemsError = false;
        this._parameters.filtersList = [];
        this._parameters.limit = 10;
        this._pagination.total = 10;
        this._pagination.page = 1;
    },

    _setKeywordsUpdatePage(page) {
        this._parameters.page = page;
    },

    _setKeywordsUpdateLength(length) {
        this._parameters.limit = length;
    },

    _setKeywordsUpdateStatus(status) {
        if (status === 'all') {
            this._parameters.filtersList = [];
            return;
        }

        this._parameters.filtersList = [
            {
                filters: [
                    {
                        key: 'keywords.is_active',
                        operator: 'is_any',
                        value: status === 'on' ? 1 : 0,
                    },
                ],
            },
        ];
    },

    _setKeywordsUpdateSearch(search) {
        this._parameters.term = search;
        this._parameters.page = 1;
    },

    _setKeywordsSearchCleared() {
        this._parameters.term = null;
    },

    _setKeywordsBulkDeleted() {},

    _setKeywordContactsLoaded({ data, id }) {
        this._keywordContactsId = id || this._keywordContactsId;
        this._keywordContacts = new Contacts(data.data).models;
        this._keywordContactsPagination = {
            type: '',
            current_page: data.current_page,
            from: data.from,
            last_page: data.last_page,
            per_page: data.per_page,
            to: data.to,
            total: data.total,
            next_page_url: data.next_page_url,
        };
    },

    _setKeywordContactsError() {
        this._keywordContacts = false;
        this._keywordContactsPagination.total = 0;
    },

    _setKeywordContactsLoading(status) {
        this._keywordContactsLoading = status;
    },

    _setKeywordContactsReset() {
        this._keywordContacts = [];
        this._keywordContactsPagination = {};
        this._keywordContactsLoading = false;
        this._keywordContactsError = false;
    },

    _setKeywordContactsUpdatePage(page) {
        this._keywordContactsParameters.page = page;
    },

    _setKeywordContactsUpdateLength(length) {
        this._keywordContactsParameters.limit = length;
    },

    _setKeywordContactsCustomUpdateLengthSelector({ page, data }) {
        this._keywordContactsParameters.page = data;
        this._keywordContactsParameters.limit = page;
    },

    _setKeywordUpdateLengthSelector({ page, data }) {
        this._parameters.page = data;
        this._parameters.limit = page;
    },

    _setKeywordContactsUpdateDate(data) {
        this._keywordContactsParameters.from = data?.start
            ? moment(data.start).startOf('minute').format('YYYY-MM-DD')
            : '';
        this._keywordContactsParameters.to = data?.end
            ? moment(data.end).startOf('minute').format('YYYY-MM-DD')
            : '';
        this._keywordContactsParameters.page = 1;
    },

    _setKeywordContactsUpdateSearch(search) {
        this._keywordContactsParameters.term = search;
        this._keywordContactsParameters.page = 1;
    },

    _setKeywordsDelete({ id, type }) {
        const item = this._items.find((item) => item.id === id);
        const index = this._items.findIndex((item) => item.id === id);

        if (!item) return;

        if (type === 'init') {
            item.disabled = true;
            Vue.set(this._items, index, item);
        }

        if (type === 'delete') {
            this._items.splice(index, 1);
        }
    },

    _setKeywordsToggle({ id, type, keyword }) {
        const item = this._items.find((item) => item.id === id);
        const index = this._items.findIndex((item) => item.id === id);

        if (type === 'init') {
            item.disabled = true;
            Vue.set(this._items, index, item);
        }

        if (type === 'update') {
            Vue.set(this._items, index, keyword);
        }
    },

    _setKeywordsUpdateSort(data) {
        this._parameters.sortBy = data.sortBy;
        this._parameters.sortOrder = data.sortOrder;
        this._items = sorting(this._items, data);
    }
};

export default {
    state,
    getters,
    actions
};
